import * as _ from 'lodash';
import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../../../globals';
import Loader from '../../../generic/Loader';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayAuthenticationSettings } from '../../shared/permissions';
import type { RouterProps } from '../../../../types';

import AuthenticationSettingsPage from './AuthenticationSettingsPage';
import { useAuthenticationSettings, useOrganizationDomains } from './hooks';
import { AuthenticationSettingsContext } from './hooks/useAuthenticationSettingsContext';
import type { AuthenticationSettingsContextValue } from './hooks/useAuthenticationSettingsContext';

type Props = {
  location?: RouterProps['location'];
  router: InjectedRouter;
};

const AuthenticationSettings: VoidFunctionComponent<Props> = ({ location, router }) => {
  const authenticationSettingsValues = useAuthenticationSettings(window.user?.organizationId as number);
  const organizationDomainsValues = useOrganizationDomains(window.user?.organizationId as number);
  const value = useMemo(
    () => ({
      ..._.omit(authenticationSettingsValues, ['isLoading']),
      ..._.omit(organizationDomainsValues, ['isLoading']),
      location,
      router,
    }),
    [authenticationSettingsValues, organizationDomainsValues, location, router],
  );

  if (authenticationSettingsValues.isLoading || organizationDomainsValues.isLoading) {
    return <Loader />;
  }
  // Prevent the page from rendering if the relevant data is missing.
  if (!value.settings || !value.domains) {
    return null;
  }
  return (
    <AuthenticationSettingsContext.Provider value={value as AuthenticationSettingsContextValue}>
      <AuthenticationSettingsPage />
    </AuthenticationSettingsContext.Provider>
  );
};

/**
 * AuthenticationSettingsContainer displays the authentication settings page if the user has permission to view it.
 */
const AuthenticationSettingsContainer: VoidFunctionComponent<Props> = ({ location, router }) => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayAuthenticationSettings}>
    <AuthenticationSettings location={location} router={router} />
  </OrganizationSettingsPermissionGate>
);

export default AuthenticationSettingsContainer;
